import { printHTML, downloadWord } from '../utils/printHelper';
import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Printer, Plus, Trash, ArrowsClockwise, FileDoc } from '@phosphor-icons/react';
import { api } from '../App';
import { DateInput } from '../components/DateInput';
import { toast } from 'sonner';
import ClientAutocomplete from '../components/ClientAutocomplete';

const STORAGE_KEY = 'shared_odluka_data';

function loadShared() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; }
}
function saveShared(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function OdlukaOdmorPage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    naziv_drustva: '',
    grad: 'Ulcinj',
    objekat_naziv: '',
    datum: new Date().toISOString().split('T')[0],
    period_od: '',
    period_do: '',
    direktor: '',
  });
  const [radnici, setRadnici] = useState([
    { ime_prezime: '', radno_mjesto: '', dan_odmora: '' }
  ]);
  const [loadingRadnici, setLoadingRadnici] = useState(false);

  useEffect(() => {
    api.get('/clients')
      .then(r => {
        const sorted = r.data.sort((a, b) => (a.skraceni_naziv || '').localeCompare(b.skraceni_naziv || ''));
        setClients(sorted);

        const shared = loadShared();
        if (shared) {
          if (shared.client_id) {
            const c = sorted.find(cl => cl.id === (shared.form?.client_id || shared.client_id));
            setForm(prev => ({
              ...prev,
              ...shared.form,
              naziv_drustva: c ? (c.skraceni_naziv || c.naziv_firme || '') : (shared.form?.naziv_drustva || ''),
            }));
          }
          if (shared.radnici?.length) {
            setRadnici(shared.radnici.map(r => ({
              ime_prezime: r.ime_prezime || '',
              radno_mjesto: r.radno_mjesto || '',
              dan_odmora: r.dan_odmora || '',
            })));
          }
        }
      });
  }, []);

  useEffect(() => {
    if (form.client_id) {
      const shared = loadShared() || {};
      saveShared({ ...shared, form, radnici, client_id: form.client_id });
    }
  }, [form, radnici]);

  const fetchAktivniRadnici = async (clientId) => {
    setLoadingRadnici(true);
    try {
      const r = await api.get(`/prijava-radnika?client_id=${clientId}&status=aktivan`);
      const workers = r.data;
      if (workers.length > 0) {
        setRadnici(workers.map(w => ({
          ime_prezime: `${(w.prezime || '').toUpperCase()} ${(w.ime || '').toUpperCase()}`.trim(),
          radno_mjesto: w.naziv_radnog_mjesta || '',
          dan_odmora: '',
        })));
        toast.success(`Učitano ${workers.length} aktivnih radnika`);
      } else {
        toast.info('Nema aktivnih radnika za ovog klijenta');
      }
    } catch {
      toast.error('Greška pri učitavanju radnika');
    } finally {
      setLoadingRadnici(false);
    }
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find(cl => cl.id === clientId);
    if (c) {
      setForm(prev => ({
        ...prev,
        client_id: clientId,
        naziv_drustva: c.skraceni_naziv || c.naziv_firme || '',
        grad: c.grad || 'Ulcinj',
        objekat_naziv: c.skraceni_naziv || c.naziv_firme || '',
        direktor: c.direktor || '',
      }));
      fetchAktivniRadnici(clientId);
    }
  };

  const addRadnik = () => setRadnici([...radnici, { ime_prezime: '', radno_mjesto: '', dan_odmora: '' }]);
  const removeRadnik = (i) => setRadnici(radnici.filter((_, idx) => idx !== i));
  const updateRadnik = (i, field, value) => {
    const updated = [...radnici];
    updated[i][field] = value;
    setRadnici(updated);
  };

  const formatDatePrint = (d) => {
    if (!d) return '___________';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  const handlePrint = () => {
    const filledRadnici = radnici.filter(r => r.ime_prezime.trim());
    const tableRows = filledRadnici.map((r, i) => `
      <tr>
        <td style="text-align:center;padding:4px 6px;border:1px solid #000">${i + 1}.</td>
        <td style="padding:4px 6px;border:1px solid #000">${r.ime_prezime || ''} ${r.radno_mjesto ? '- ' + r.radno_mjesto : ''}</td>
        <td style="text-align:center;padding:4px 6px;border:1px solid #000">${r.dan_odmora || ''}</td>
      </tr>
    `).join('');

    const periodText = form.period_od && form.period_do
      ? `${formatDatePrint(form.period_od)} - ${formatDatePrint(form.period_do)}`
      : '________________________';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Odluka o sedmicnom odmoru</title>
<style>
@page{size:A4;margin:20mm 25mm}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:12pt;color:#000;line-height:1.6;background:#fff}
.doc{max-width:170mm;margin:0 auto}
.header{font-size:11pt;margin-bottom:5px}
.header-firm{font-weight:bold;font-size:12pt}
.header-row{display:flex;justify-content:space-between;font-size:11pt;margin:3px 0}
.header-line{border-bottom:1px solid #000;min-width:150px;display:inline-block;padding:0 4px}
.title{text-align:center;font-weight:bold;font-size:14pt;margin:20px 0 5px}
.subtitle{text-align:center;font-size:11pt;margin-bottom:15px}
p{font-size:11pt;line-height:1.5;margin:6px 0;text-align:justify}
table.workers{width:100%;border-collapse:collapse;margin:10px 0;font-size:10.5pt}
table.workers th{background:#f0f0f0;font-weight:bold;padding:5px 6px;border:1px solid #000;font-size:10pt;text-align:left}
table.workers td{font-size:10.5pt}
.period{margin:15px 0;font-size:11pt}
.obrazlozenje{margin-top:15px}
.obrazlozenje-title{font-weight:bold;font-size:11pt;text-decoration:underline;margin-bottom:5px}
.dostavljeno{margin-top:15px;font-size:10.5pt;line-height:1.5}
.sign-area{display:flex;justify-content:flex-end;margin-top:30px;font-size:11pt}
.sign-right{text-align:center}
.sign-line{border-top:1px solid #000;min-width:180px;padding-top:3px;margin-top:30px}
@media print{body{margin:0}table.workers th{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="doc">
<div class="header">
  <div class="header-firm">&bdquo;${form.naziv_drustva}&ldquo;</div>
  <div class="header-row">
    <span>Broj: <span class="header-line">&nbsp;</span></span>
  </div>
  <div class="header-row">
    <span>${form.grad.toUpperCase()}: <span class="header-line">${formatDatePrint(form.datum)}</span> god.</span>
  </div>
</div>

<p style="margin-top:10px;font-size:10.5pt">Na osnovu člana 76 Zakona o radu ZAKON O RADU (&bdquo;Sl. list CG&ldquo;, br.74/2019, 8/2021, 59/2021, 68/2021, 145/2021, 77/2024, 84/2024 - drugi zakon, 86/2024, 122/2025 i 165/2025) &bdquo;${form.naziv_drustva}&ldquo;</p>

<div class="title">ODLUKU</div>
<div class="subtitle">- o utvrđivanju rasporeda korišćenja sedmičnog odmora -</div>

<p>Zaposlenima <strong>${form.objekat_naziv}</strong> utvrđuje se pravo na sedmični odmor u trajanju od najmanje 24 časa neprekidno.</p>
<p>Obzirom da priroda posla i organizacija rada to zahtijevaju zaposleni neće koristiti sedmični odmor po pravilu nedjeljom, već u danima, kako se prikazuje u evidenciji zaposlenih sa određenjem dana korišćenja sedmičnog odmora.</p>

<table class="workers">
  <thead>
    <tr>
      <th style="width:40px;text-align:center">red.<br>br.</th>
      <th>ime i prezime zaposlenog i radno mjesto na koje je zaključio ugovor o radu</th>
      <th style="width:120px;text-align:center">dan korišćenja sedmičnog odmora</th>
    </tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>

<div class="period">Odluka se donosi na period <strong>${periodText}</strong>.</div>

<div class="obrazlozenje">
  <div class="obrazlozenje-title">Obrazloženje</div>
  <p style="font-size:10.5pt">Odredbama člana 76 Zakona o radu ZAKON O RADU (&bdquo;Sl. list CG&ldquo;, br.74/2019, 8/2021, 59/2021, 68/2021, 145/2021, 77/2024, 84/2024 - drugi zakon, 86/2024, 122/2025 i 165/2025), propisano je da zaposleni ima pravo na sedmični odmor u trajanju od najmanje 24 časa neprekidno, i da se sedmični odmor po pravilu koristi nedjeljom. Nadalje stavom 3 istog člana je propisano da ako priroda posla i organizacija rada to zahtijeva poslodavac je dužan da odredi drugi dan za korišćenje sedmičnog odmora. Na osnovu iznijetog, a shodno odredbama člana 76 Zakona o radu, riješeno je kao u dispozitivu odluke.</p>
</div>

<div class="dostavljeno">
  Dostavljeno:<br>
  - zaposlenima,<br>
  - na oglasnoj tabli poslodavca,<br>
  - a/a.
</div>

<div class="sign-area">
  <div class="sign-right">
    POSLODAVAC
    <div class="sign-line">_____________</div>
  </div>
</div>
</div></body></html>`;
    printHTML(html);
  };

  const handleWord = () => {
    const filledRadnici = radnici.filter(r => r.ime_prezime.trim());
    if (filledRadnici.length === 0) return;
    const tableRows = filledRadnici.map((r, i) => `<tr><td style="text-align:center;border:1px solid #000;padding:4px">${i+1}.</td><td style="border:1px solid #000;padding:4px">${r.ime_prezime||''} ${r.radno_mjesto?'- '+r.radno_mjesto:''}</td><td style="text-align:center;border:1px solid #000;padding:4px">${r.dan_odmora||''}</td></tr>`).join('');
    const periodText = form.period_od && form.period_do ? `${formatDatePrint(form.period_od)} - ${formatDatePrint(form.period_do)}` : '____';
    const html2 = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:Arial;font-size:12pt}table{width:100%;border-collapse:collapse}th,td{border:1px solid #000;padding:4px}</style></head><body><h2 style="text-align:center">ODLUKU - o utvrđivanju rasporeda korišćenja sedmičnog odmora</h2><p>${form.naziv_drustva}, ${form.grad}</p><table><thead><tr><th>R.br.</th><th>Ime i prezime i radno mjesto</th><th>Dan odmora</th></tr></thead><tbody>${tableRows}</tbody></table><p>Period: ${periodText}</p></body></html>`;
    downloadWord(html2, `Sedmicni_odmor_${(form.naziv_drustva||'').replace(/\s+/g,'_')}.docx`);
  };

  return (
    <div className="space-y-4" data-testid="odluka-odmor-page">
      <Card className="p-4 bg-white border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Odluka o sedmičnom odmoru</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Klijent</Label>
            <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => (handleClientSelect)(id || 'none')} testid="odmor-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Naziv objekta</Label>
            <Input value={form.objekat_naziv} onChange={e => setForm({...form, objekat_naziv: e.target.value})} className="bg-white border-slate-300 text-sm" data-testid="odmor-objekat-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Grad</Label>
            <Input value={form.grad} onChange={e => setForm({...form, grad: e.target.value})} className="bg-white border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Datum odluke</Label>
            <DateInput value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} className="border-slate-300 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Period od</Label>
            <DateInput value={form.period_od} onChange={e => setForm({...form, period_od: e.target.value})} className="border-slate-300 text-sm" data-testid="odmor-period-od" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Period do</Label>
            <DateInput value={form.period_do} onChange={e => setForm({...form, period_do: e.target.value})} className="border-slate-300 text-sm" data-testid="odmor-period-do" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Direktor</Label>
            <Input value={form.direktor} readOnly className="bg-slate-50 border-slate-300 text-sm" />
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-white border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-700">Spisak zaposlenih</h3>
          <div className="flex gap-2">
            {form.client_id && (
              <Button onClick={() => fetchAktivniRadnici(form.client_id)} variant="outline" size="sm" className="text-xs border-blue-300 text-blue-600 hover:bg-blue-50" disabled={loadingRadnici}>
                <ArrowsClockwise size={14} className={`mr-1 ${loadingRadnici ? 'animate-spin' : ''}`} /> {loadingRadnici ? 'Učitavam...' : 'Osvježi iz baze'}
              </Button>
            )}
            <Button onClick={addRadnik} variant="outline" size="sm" className="text-xs" data-testid="odmor-add-radnik">
              <Plus size={14} className="mr-1" /> Dodaj radnika
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {radnici.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_140px_36px] gap-2 items-center">
              <Input
                value={r.ime_prezime}
                onChange={e => updateRadnik(i, 'ime_prezime', e.target.value)}
                placeholder="Ime i prezime"
                className="bg-white border-slate-300 text-sm"
                data-testid={`odmor-radnik-ime-${i}`}
              />
              <Input
                value={r.radno_mjesto}
                onChange={e => updateRadnik(i, 'radno_mjesto', e.target.value)}
                placeholder="Radno mjesto"
                className="bg-white border-slate-300 text-sm"
              />
              {/* Multi-day rest picker: pop-over with checkboxes so user can
                  select more than one day (e.g. "Subota, Nedjelja"). dan_odmora
                  is stored as comma-separated string. */}
              <div className="relative">
                <details className="group">
                  <summary className="list-none cursor-pointer bg-white border border-slate-300 text-sm rounded-md h-9 px-3 flex items-center justify-between hover:border-slate-400">
                    <span className={r.dan_odmora ? 'text-slate-900' : 'text-slate-400'}>
                      {r.dan_odmora || 'Izaberi dan(e)'}
                    </span>
                    <svg className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                  </summary>
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg p-2 space-y-1">
                    {['Ponedeljak','Utorak','Srijeda','Četvrtak','Petak','Subota','Nedjelja'].map(d => {
                      const selected = (r.dan_odmora || '').split(',').map(s => s.trim()).filter(Boolean);
                      const isChecked = selected.includes(d);
                      const toggle = () => {
                        const next = isChecked ? selected.filter(x => x !== d) : [...selected, d];
                        // Sort by weekday order so output is stable ("Subota, Nedjelja" not "Nedjelja, Subota")
                        const order = ['Ponedeljak','Utorak','Srijeda','Četvrtak','Petak','Subota','Nedjelja'];
                        next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                        updateRadnik(i, 'dan_odmora', next.join(', '));
                      };
                      return (
                        <label key={d} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                          <input type="checkbox" checked={isChecked} onChange={toggle} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                          {d}
                        </label>
                      );
                    })}
                  </div>
                </details>
              </div>
              <button onClick={() => removeRadnik(i)} className="p-2 hover:bg-red-100 rounded" data-testid={`odmor-remove-radnik-${i}`}>
                <Trash size={14} className="text-red-500" />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-2">
        <Button onClick={handlePrint} className="bg-slate-800 hover:bg-slate-700 text-sm" disabled={!form.client_id} data-testid="print-odmor-btn">
          <Printer size={16} className="mr-2" /> Štampaj
        </Button>
        <Button onClick={handleWord} className="bg-indigo-600 hover:bg-indigo-700 text-sm text-white" disabled={!form.client_id}>
          <FileDoc size={16} className="mr-2" /> Word
        </Button>
      </div>
    </div>
  );
}
